// src/pages/personal/analytics/MarketingExpensesSection/components/ExpensesTable.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Flex } from '@/components/ui/Layout';
import { MarketingExpense, useMarketingExpenses } from '../hooks/useMarketingExpenses';
import { formatDateTime } from '@/utils/dateUtils';
import { formatCurrency } from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';
import { useResponsive } from '@/hooks/useResponsive';
import ExpenseFormModal from './ExpenseFormModal';
import styles from '../MarketingExpensesSection.module.css';

// Объявление типов для фильтров
interface ExpensesFilters {
  channel?: string;
  platform?: string;
  startDate?: string;
  endDate?: string;
}

const ExpensesTable: React.FC = () => {
  const { t } = useTranslation(['expenses', 'common']);
  const { expenses, isLoading, error, fetchExpenses, deleteExpense } = useMarketingExpenses();
  const { isMobile } = useResponsive(); // Получаем информацию о мобильном режиме
  
  // Состояние для модального окна создания/редактирования расхода
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<MarketingExpense | null>(null);
  
  // Состояния для фильтров
  const [filters, setFilters] = useState<ExpensesFilters>({});
  const [filteredExpenses, setFilteredExpenses] = useState<MarketingExpense[]>([]);
  
  // Получение списка расходов при монтировании компонента
  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);
  
  // Применение фильтров при изменении списка расходов или фильтров
  useEffect(() => {
    if (!expenses) {
      setFilteredExpenses([]);
      return;
    }
    
    // Применение фильтров
    let result = [...expenses];
    
    if (filters.channel) {
      result = result.filter(expense => expense.channel === filters.channel);
    }
    
    if (filters.platform) {
      result = result.filter(expense => expense.platform === filters.platform);
    }
    
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      result = result.filter(expense => new Date(expense.start_date) >= startDate);
    }
    
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      result = result.filter(expense => new Date(expense.end_date) <= endDate);
    }
    
    // Сортировка по дате начала (сначала новые)
    result.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());
    
    setFilteredExpenses(result);
  }, [expenses, filters]);
  
  // Обработчик изменения фильтров
  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);
  
  // Сброс фильтров
  const resetFilters = useCallback(() => {
    setFilters({});
  }, []);
  
  // Обработчик редактирования расхода
  const handleEditExpense = useCallback((expense: MarketingExpense) => {
    setSelectedExpense(expense);
    setIsModalOpen(true);
  }, []);
  
  // Обработчик удаления расхода
  const handleDeleteExpense = useCallback(async (id: number) => {
    if (window.confirm(t('common:confirm_delete'))) {
      await deleteExpense(id);
    }
  }, [deleteExpense, t]);
  
  // Обработчик создания нового расхода
  const handleCreateExpense = useCallback(() => {
    setSelectedExpense(null);
    setIsModalOpen(true);
  }, []);
  
  // Обработчик закрытия модального окна
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedExpense(null);
  }, []);
  
  // Обработчик успешного сохранения расхода
  const handleSaveSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedExpense(null);
    fetchExpenses();
  }, [fetchExpenses]);
  
  // Вспомогательная функция для получения опций каналов
  const getChannelOptions = useCallback(() => {
    if (!expenses) return [];
    const channels = [...new Set(expenses.map(e => e.channel))];
    return channels.map(channel => ({
      value: channel,
      label: t(`marketing.sources.${channel}`, { defaultValue: channel })
    }));
  }, [expenses, t]);
  
  // Вспомогательная функция для получения опций платформ
  const getPlatformOptions = useCallback(() => {
    if (!expenses) return [];
    const platforms = [...new Set(expenses.map(e => e.platform))];
    return platforms.map(platform => ({
      value: platform,
      label: t(`marketing.platforms.${platform}`, { defaultValue: platform })
    }));
  }, [expenses, t]);
  
  // Рендер карточки для мобильного представления
  const renderExpenseCard = (expense: MarketingExpense) => {
    return (
      <div key={expense.id} className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium text-gray-900 truncate max-w-[60%]">
            {expense.campaign_name || t('marketing.expenses.no_campaign_name')}
          </h3>
          <span className="font-bold text-gray-900">
            {formatCurrency(expense.amount, LOCALES.RU)}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <span className="text-xs text-gray-500 block">
              {t('marketing.expenses.channel')}
            </span>
            <span className="text-sm">
              {t(`marketing.sources.${expense.channel}`, { defaultValue: expense.channel })}
            </span>
          </div>
          
          <div>
            <span className="text-xs text-gray-500 block">
              {t('marketing.expenses.platform')}
            </span>
            <span className="text-sm">
              {t(`marketing.platforms.${expense.platform}`, { defaultValue: expense.platform })}
            </span>
          </div>
        </div>
        
        <div className="mb-3">
          <span className="text-xs text-gray-500 block">
            {t('marketing.expenses.dates')}
          </span>
          <span className="text-sm">
            {formatDateTime(expense.start_date)} - {formatDateTime(expense.end_date)}
          </span>
        </div>
        
        <div className="flex justify-end gap-2 mt-2">
          <button
            className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md text-sm"
            onClick={() => handleEditExpense(expense)}
          >
            {t('common:edit')}
          </button>
          <button
            className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm"
            onClick={() => handleDeleteExpense(expense.id!)}
          >
            {t('common:delete')}
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <Card padding="md" shadow="sm">
      <h3 className="text-xl font-medium text-gray-800 mb-4">
        {t('marketing.expenses.list_title')}
      </h3>
      
      {/* Панель с фильтрами и кнопкой добавления */}
      <Flex 
        justify="between" 
        align="center" 
        className="mb-4"
        mobileDirection="column" 
        mobileGap="md"
      >
        {/* Фильтры */}
        <Flex gap="sm" wrap className="w-full md:w-auto" mobileDirection="column">
          <select
            name="channel"
            value={filters.channel || ''}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">{t('marketing.filters.all_channels')}</option>
            {getChannelOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <select
            name="platform"
            value={filters.platform || ''}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">{t('marketing.filters.all_platforms')}</option>
            {getPlatformOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <input
            type="date"
            name="startDate"
            value={filters.startDate || ''}
            onChange={handleFilterChange}
            placeholder={t('marketing.filters.from_date')}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          
          <input
            type="date"
            name="endDate"
            value={filters.endDate || ''}
            onChange={handleFilterChange}
            placeholder={t('marketing.filters.to_date')}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md text-sm hover:bg-gray-300"
          >
            {t('marketing.filters.reset')}
          </button>
        </Flex>
        
        {/* Кнопка добавления */}
        <button
          onClick={handleCreateExpense}
          className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 w-full md:w-auto"
        >
          {t('marketing.expenses.add')}
        </button>
      </Flex>
      
      {/* Содержимое таблицы или карточек */}
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>{t('common:loading')}</p>
        </div>
      ) : error ? (
        <div className={styles.errorMessage}>{error}</div>
      ) : filteredExpenses.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {t('marketing.expenses.no_data')}
        </div>
      ) : isMobile ? (
        // Мобильное представление в виде карточек
        <div className="space-y-4">
          {filteredExpenses.map(renderExpenseCard)}
        </div>
      ) : (
        // Десктопное представление в виде таблицы
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('marketing.expenses.channel')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('marketing.expenses.platform')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('marketing.expenses.campaign')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('marketing.expenses.dates')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('marketing.expenses.amount')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common:actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredExpenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {t(`marketing.sources.${expense.channel}`, { 
                      defaultValue: expense.channel 
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {t(`marketing.platforms.${expense.platform}`, { 
                      defaultValue: expense.platform 
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {expense.campaign_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(expense.start_date)} - {formatDateTime(expense.end_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(expense.amount, LOCALES.RU)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Flex justify="end" gap="sm">
                      <button
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={() => handleEditExpense(expense)}
                      >
                        {t('common:edit')}
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleDeleteExpense(expense.id!)}
                      >
                        {t('common:delete')}
                      </button>
                    </Flex>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Модальное окно создания/редактирования расхода */}
      <ExpenseFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleSaveSuccess}
        expense={selectedExpense}
      />
    </Card>
  );
};

export default ExpensesTable;